import { useTranslation } from 'react-i18next';
import { maxVoyageDelay } from './ShipMarker';

const BADGE = {
  delayed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  ahead: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  onTime: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  estimated: 'bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-300',
  arrived: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  atPort: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

/** Format N as integer if whole, else 1 decimal. Do not coerce null to 0. */
export function formatDelayN(delayDays) {
  if (delayDays === null || delayDays === undefined || delayDays === '') return null;
  const n = Number(delayDays);
  if (Number.isNaN(n)) return null;
  const rounded = Math.round(n);
  if (Math.abs(n - rounded) < 0.05) return String(Math.abs(rounded));
  return Math.abs(n).toFixed(1);
}

function hasSchedule(value) {
  if (value === null || value === undefined) return false;
  const s = String(value).trim();
  return s !== '' && s.toLowerCase() !== 'null';
}

/**
 * Delayed if max(row delay_days, any voyage_stops delay_days) >= 1.
 * Ahead only if that max <= -1. Never On time when any stop is delayed.
 */
export default function StatusBadge({
  status,
  delayDays,
  etaHasUpdate,
  etaUpdated,
  etaInitial,
  trackingMethod,
  voyageStops,
  shipment,
}) {
  const { t } = useTranslation();
  const fromStops = shipment
    ? maxVoyageDelay(shipment)
    : maxVoyageDelay({ delay_days: delayDays, voyage_stops: voyageStops ?? [] });
  const d = fromStops;
  const method = String(trackingMethod || '').toLowerCase();
  const liveProvider = method === 'shipsgo' || method === 'marinetraffic';
  const n = formatDelayN(d);
  const providerSchedule = hasSchedule(etaUpdated) || hasSchedule(etaInitial) || etaHasUpdate === true;

  let label;
  let cls;

  if (d !== null && d >= 1) {
    label = `${t('seaTracking.status.delayed')} +${n}d`;
    cls = BADGE.delayed;
  } else if (d !== null && d <= -1) {
    label = `${t('seaTracking.status.ahead')} \u2212${String(n).replace(/^-/, '')} d`;
    cls = BADGE.ahead;
  } else if (status === 'arrived') {
    label = t('seaTracking.status.arrived');
    cls = BADGE.arrived;
  } else if (status === 'at_port') {
    label = t('seaTracking.status.atPort');
    cls = BADGE.atPort;
  } else if (method === 'searoute' || !providerSchedule) {
    label = t('seaTracking.status.estimatedBadge');
    cls = BADGE.estimated;
  } else if (liveProvider && d !== null && Math.abs(d) < 1) {
    label = t('seaTracking.status.onTime');
    cls = BADGE.onTime;
  } else {
    label = t('seaTracking.status.estimatedBadge');
    cls = BADGE.estimated;
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}
